import { useCallback } from "react";
import type { Integration } from "app/pages/sales-intelligence/sub-modules/management/types";
import { useDispatch, useSelector } from "react-redux";
import {
    selectHsExcludeAccountFields,
    selectHsIsLoadingExcludeAccountFields,
    selectHsIntegrationLeadFilterStatus,
    selectHsFilterFieldNames,
    selectIsUpdatingHsFilterFields,
} from "app/pages/sales-intelligence/sub-modules/management/store/selectors/hubspotSelectors";
import {
    selectSfExcludeAccountFields,
    selectSfIsLoadingExcludeAccountFields,
    selectSfIntegrationLeadFilterStatus,
    selectSfFilterFieldNames,
    selectIsUpdatingSfFilterFields,
} from "app/pages/sales-intelligence/sub-modules/management/store/selectors/salesforceSelectors";
import {
    selectDynamicsExcludeAccountFields,
    selectDynamicsIsLoadingExcludeAccountFields,
    selectDynamicsIntegrationLeadFilterStatus,
    selectDynamicsFilterFieldNames,
    selectIsUpdatingDynamicsFilterFields,
} from "app/pages/sales-intelligence/sub-modules/management/store/selectors/dynamicsSelectors";
import { putSfFilterFieldsThunk } from "app/pages/sales-intelligence/sub-modules/management/store/effects/salesforceApi";
import { putHsExcludeThunk } from "app/pages/sales-intelligence/sub-modules/management/store/effects/hubspot/exclude";
import { putSfExcludeThunk } from "app/pages/sales-intelligence/sub-modules/management/store/effects/salesforce/exclude";
import { putDynamicsExcludeThunk } from "app/pages/sales-intelligence/sub-modules/management/store/effects/dynamics/exclude";
import { putDynamicsFilterFieldsThunk } from "app/pages/sales-intelligence/sub-modules/management/store/effects/dynamicsApi";
import type { ExcludeAccountFields } from "app/pages/sales-intelligence/common-components/export-table/types";
import { putHsFilterFieldsThunk } from "app/pages/sales-intelligence/sub-modules/management/store/effects/hubspotApi";

export function useCrmFiltersProps(integrationName: Integration): {
    accountFields: ExcludeAccountFields;
    isLoadingAccountFields: boolean;
    integrationStatus: boolean | null;
    filterFields: string[];
    isFilterFieldsLoading: boolean;
    putIntegrationStatus: (payload: boolean) => Promise<void>;
    putFilterFields: (fields: string[]) => Promise<boolean>;
} {
    const dispatch = useDispatch();

    const propsByIntegration = {
        hubspot: {
            accountFields: selectHsExcludeAccountFields,
            isLoadingAccountFields: selectHsIsLoadingExcludeAccountFields,
            integrationStatus: selectHsIntegrationLeadFilterStatus,
            filterFields: selectHsFilterFieldNames,
            isUpdatingFilterFields: selectIsUpdatingHsFilterFields,
        },
        salesforce: {
            accountFields: selectSfExcludeAccountFields,
            isLoadingAccountFields: selectSfIsLoadingExcludeAccountFields,
            integrationStatus: selectSfIntegrationLeadFilterStatus,
            filterFields: selectSfFilterFieldNames,
            isUpdatingFilterFields: selectIsUpdatingSfFilterFields,
        },
        dynamics: {
            accountFields: selectDynamicsExcludeAccountFields,
            isLoadingAccountFields: selectDynamicsIsLoadingExcludeAccountFields,
            integrationStatus: selectDynamicsIntegrationLeadFilterStatus,
            filterFields: selectDynamicsFilterFieldNames,
            isUpdatingFilterFields: selectIsUpdatingDynamicsFilterFields,
        },
    };

    const accountFields = useSelector(propsByIntegration[integrationName].accountFields);
    const isLoadingAccountFields = useSelector(propsByIntegration[integrationName].isLoadingAccountFields);
    const integrationStatus = useSelector(propsByIntegration[integrationName].integrationStatus);
    const filterFields = useSelector(propsByIntegration[integrationName].filterFields);
    const isUpdatingFilterFields = useSelector(propsByIntegration[integrationName].isUpdatingFilterFields);

    const putFilterFields = (fields: string[]) => {
        if (integrationName === "hubspot") {
            return dispatch(putHsFilterFieldsThunk(fields));
        }
        if (integrationName === "dynamics") {
            return dispatch(putDynamicsFilterFieldsThunk(fields));
        }
        return dispatch(putSfFilterFieldsThunk(fields));
    };

    const putIntegrationStatus = useCallback(
        (status: boolean) => {
            if (integrationName === "hubspot") {
                return dispatch(putHsExcludeThunk(status));
            }
            if (integrationName === "dynamics") {
                return dispatch(putDynamicsExcludeThunk(status));
            }

            return dispatch(putSfExcludeThunk(status));
        },
        [dispatch, integrationName],
    );

    return {
        accountFields,
        isLoadingAccountFields,
        integrationStatus,
        putIntegrationStatus,
        putFilterFields,
        filterFields,
        isFilterFieldsLoading: isUpdatingFilterFields,
    };
}
